import {useState} from 'react'
import {useNavigate} from 'react-router'
import {useSafeCallbackExtraDeps} from '@freckle/react-hooks'
import type {CallbackFn} from '@freckle/react-hooks'
import {unCallbackFn} from '@freckle/react-hooks'
import {HttpError} from '@freckle/educator-entities/ts/common/exceptions/http-error'
import type {LinksT} from '@freckle/educator-entities/ts/common/helpers/ajax'
import type {OperationLocationT} from '@freckle/educator-entities/ts/common/helpers/ajax'
import {useOperationPersistence} from '@freckle/educator-entities/ts/common/hooks/operations/use-operation-persistence'
import type {SchoolAttrs} from '@freckle/educator-entities/ts/roster/models/school'
import {Button} from '@freckle/educator-materials/ts/components/button'
import {ButtonAsync} from '@freckle/educator-materials/ts/components/button-async'
import {Flex} from '@freckle/educator-materials/ts/components/flex'
import {SpinnerOverlay} from '@freckle/educator-materials/ts/components/spinner'
import {H3} from '@freckle/educator-materials/ts/components/typography'
import {useSafeTranslation} from '@freckle/educator-materials/ts/helpers/translate'
import {PendingOperationWhenLocation} from '@freckle/school/ts/common/components/common/pending-operation'
import {logErrorAsUnhandled} from '@freckle/school/ts/common/helpers/exception-handlers/bugsnag-client'
import Notification from '@freckle/school/ts/common/helpers/notification'
import {Routes} from '@freckle/school/ts/common/routers/routes'
import {
  fetchTeacherTableRows,
  nonIdpTeachersFilter,
  removeAffiliations,
} from '@freckle/school/ts/manage-teachers/api-helpers/api-teacher-table'
import type {TableParamsT} from '@freckle/school/ts/manage-teachers/components/api-controlled-table'
import type {TeacherTabFilteringT} from '@freckle/school/ts/manage-teachers/components/teacher-tab/'
import type {SelfRosteredTableRowT} from './self-rostered-teacher-table'
import SelfRosteredTeacherTable from './self-rostered-teacher-table'
import {
  consentWrapper,
  consentText,
  descriptionWrapper,
} from './review-self-rostered-teachers.module.scss'

export type TeacherSelectionsT = {
  selected: Set<number>
  selectAll: boolean
  totalTeachersSelected: number
  totalCount: number
}

type Props = {
  currentSchoolIds: Array<number> | undefined | null
  schools: Array<SchoolAttrs>
  tableParams: TableParamsT<TeacherTabFilteringT>
  setRemovedSelfRosteredTeachersCB: CallbackFn<(totalRemoved: number) => void>
  onRemoveAffiliationSuccess: CallbackFn<() => void>
}

const PERSISTENCE_KEY = 'remove-self-rostered-teachers'

export function ReviewSelfRosteredTeachers(props: Props) {
  const {
    currentSchoolIds,
    schools,
    tableParams,
    setRemovedSelfRosteredTeachersCB,
    onRemoveAffiliationSuccess,
  } = props
  const {t} = useSafeTranslation()
  const routerHistory = useNavigate()
  const operationPersistence = useOperationPersistence()
  const locationFromPersistence = operationPersistence.pendingLocationFor(PERSISTENCE_KEY)
  const [removeAffiliationInProgress, setRemoveAffiliationInProgress] = useState(
    locationFromPersistence !== null,
  )
  const [bulkUnaffiliationOperationLocation, setBulkUnaffiliationOperationLocation] =
    useState<OperationLocationT | null>(locationFromPersistence)
  const [consent, setConsent] = useState(false)
  const [totalSelfRosteredTeacherCount, setTotalSelfRosteredTeacherCount] = useState(0)
  const [selectedTeachersSet, setSelectedTeachersSet] = useState(new Set<number>())
  const [allTeachersSelected, setAllTeachersSelected] = useState(true)

  const onFinishCB = useSafeCallbackExtraDeps(
    ({persistence}: {persistence: typeof operationPersistence}) =>
      () =>
        persistence.remove(PERSISTENCE_KEY),
    [],
    {persistence: {value: operationPersistence, comparator: () => true}},
  )

  const onErrorCB = useSafeCallbackExtraDeps<
    () => void,
    {setRemovedSelfRosteredTeachers: (totalRemoved: number) => void}
  >(
    ({setRemovedSelfRosteredTeachers}) =>
      () => {
        Notification.displayErrorNotification(
          'Could not remove self-rostered teachers. Please try again later.',
        )
        setRemoveAffiliationInProgress(false)
        setRemovedSelfRosteredTeachers(0)
      },
    [],
    {
      setRemovedSelfRosteredTeachers: setRemovedSelfRosteredTeachersCB,
    },
  )

  const setRemovedSelfRosteredTeachers = unCallbackFn(setRemovedSelfRosteredTeachersCB)

  const onError = unCallbackFn(onErrorCB)

  const allSchoolIds = schools.map(s => s.id)
  const schoolIdsToUse = currentSchoolIds ?? allSchoolIds

  const onToggleConsent = () => setConsent(prevConsent => !prevConsent)

  const fetchTableRows = (
    newTableParams: TableParamsT<TeacherTabFilteringT>,
  ): Promise<{
    links: LinksT
    rows: Array<SelfRosteredTableRowT>
    totalCount: number
  }> => {
    const filterToNoIdpParams: TableParamsT<TeacherTabFilteringT> = {
      ...newTableParams,
      additionalFilters: {...newTableParams.additionalFilters, ...nonIdpTeachersFilter},
    }
    return fetchTeacherTableRows(schoolIdsToUse, filterToNoIdpParams).then(
      ({teacherTableRows, links, totalCount}) => {
        const rows = teacherTableRows.map(teacherTableRow => {
          const {firstName, lastName, ...rest} = teacherTableRow
          const name = [firstName, lastName].join(' ')
          return {
            name,
            ...rest,
          }
        })
        if (allTeachersSelected) {
          //update selected teachers list with newly fetched rows
          const newRowIds = rows.map(row => row.id)
          setSelectedTeachersSet(
            prevSelectedTeachersList => new Set([...prevSelectedTeachersList, ...newRowIds]),
          )
        }
        setTotalSelfRosteredTeacherCount(totalCount)

        return {links, rows, totalCount}
      },
    )
  }

  const onUpdate = (newTableParams: TableParamsT<TeacherTabFilteringT>) => {
    const url = Routes.rostering.removeSelfRosteredTeachers({
      schoolIds: currentSchoolIds,
      position: newTableParams.position,
      searchValue: newTableParams.searchValue,
      limit: newTableParams.limit,
    })

    routerHistory(url)
  }

  const onUpdateRowSelection = (id: number, isSelected: boolean) => {
    if (isSelected) {
      setSelectedTeachersSet(prevTeachersSelectedSet => new Set([...prevTeachersSelectedSet, id]))
    } else {
      //remove from selected array
      setSelectedTeachersSet(
        prevTeachersSelectedList =>
          new Set(Array.from(prevTeachersSelectedList).filter(prevId => prevId !== id)),
      )
      setAllTeachersSelected(false)
    }
  }

  const getTeacherSelections = (): TeacherSelectionsT => {
    const totalTeachersSelected = getTotalTeachersSelected()
    return {
      selected: selectedTeachersSet,
      selectAll: allTeachersSelected,
      totalCount: totalSelfRosteredTeacherCount,
      totalTeachersSelected,
    }
  }

  const returnToCleanUpRoster = () => routerHistory(Routes.rostering.cleanUpRosterLink())

  const onRemoveAffiliations: () => Promise<void> = async () => {
    setRemoveAffiliationInProgress(true)
    const selectedAllTeachersParam = {tag: 'non-idp-teachers' as const, schoolIds: schoolIdsToUse}
    const specificTeachersParam = {
      tag: 'teachers.id' as const,
      teacherIds: Array.from(selectedTeachersSet),
    }
    try {
      const {teacherIds, operationLocation} = await removeAffiliations(
        allTeachersSelected ? selectedAllTeachersParam : specificTeachersParam,
      )
      const affiliationsRemoved = teacherIds.length

      operationPersistence.track(PERSISTENCE_KEY, operationLocation)
      setBulkUnaffiliationOperationLocation(operationLocation)
      setRemovedSelfRosteredTeachers(affiliationsRemoved)
    } catch (e) {
      logErrorAsUnhandled(new HttpError(e))
      onError()
    }
  }

  const getTotalTeachersSelected = () =>
    allTeachersSelected ? totalSelfRosteredTeacherCount : selectedTeachersSet.size

  const pendingState = removeAffiliationInProgress && (
    <>
      {bulkUnaffiliationOperationLocation === null && <SpinnerOverlay />}
      <PendingOperationWhenLocation
        operationLocation={bulkUnaffiliationOperationLocation}
        onSuccess={onRemoveAffiliationSuccess}
        onFailure={onErrorCB}
        onFinish={onFinishCB}
        i18nKeys={{
          title: 'BULK_UNAFFILIATION_PENDING_TITLE',
          heading: 'BULK_UNAFFILIATION_PENDING_HEADING',
          body: 'BULK_UNAFFILIATION_PENDING_BODY',
        }}
      />
    </>
  )

  const onToggleSelectAll = (selectAll: boolean, allRows: {id: number}[]) => {
    setAllTeachersSelected(selectAll)
    setSelectedTeachersSet(selectAll ? new Set(allRows.map(row => row.id)) : new Set())
  }

  return (
    <>
      {pendingState}
      <H3>{t('REVIEW_SELF_ROSTERED_TEACHERS_H3_HEADING')}</H3>
      <p className={descriptionWrapper}>{t('REVIEW_SELF_ROSTERED_TEACHERS_DESCRIPTION')}</p>
      <SelfRosteredTeacherTable
        tableParams={tableParams}
        fetchTableRows={fetchTableRows}
        onUpdate={onUpdate}
        onUpdateRowSelection={onUpdateRowSelection}
        getTeacherSelections={getTeacherSelections}
        onToggleSelectAll={onToggleSelectAll}
      />
      <Flex className={consentWrapper}>
        <input type="checkbox" checked={consent} onChange={onToggleConsent} />
        <p className={consentText}>{t('REVIEW_SELF_ROSTERED_TEACHERS_CONSENT')}</p>
      </Flex>
      <Flex>
        <ButtonAsync
          style="primary"
          disabled={!consent || getTotalTeachersSelected() === 0}
          onClick={onRemoveAffiliations}
          dataTest="remove-self-rostered-teachers-btn"
        >
          {t('REVIEW_SELF_ROSTERED_TEACHERS_BUTTON_REMOVE_AFFILIATIONS', {
            numSelectedTeachers: getTotalTeachersSelected(),
          })}
        </ButtonAsync>
        <Button
          style="secondary"
          onClick={returnToCleanUpRoster}
          dataTest="cancel-remove-self-rostered-teachers-btn"
        >
          {t('REVIEW_SELF_ROSTERED_TEACHERS_BUTTON_CANCEL')}
        </Button>
      </Flex>
    </>
  )
}
